import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Chip,
  LinearProgress,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  FlightLand,
  MeetingRoom,
  TrendingDown,
  TrendingFlat,
  TrendingUp,
} from '@mui/icons-material';
import moment from 'moment';
import {
  buildDayStat,
  buildMonthCalendarModel,
  formatCellOccupancyLabel,
  formatDaySummary,
  formatLoadPctLabel,
  getDayTrend,
  getLoadPctRaw,
  mobileDarkOccupancyCellColors,
  mobileDarkOccupancyLegend,
} from '../booking/bookingCalendarMonthUtils';
import { mobileAppGlassCard } from '../layout/mobile/mobileAppTheme';
import { LOGIN_ACCENT } from '../auth/loginDarkTheme';

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export default function MobileBookingCalendarMonth({
  data,
  inventoryByDate = null,
  anchorDate,
  onDateSelect,
}) {
  const totalRooms = data?.rooms?.length ?? 0;
  const legendItems = mobileDarkOccupancyLegend();
  const todayStr = moment().format('YYYY-MM-DD');
  const [selectedKey, setSelectedKey] = useState(() => moment(anchorDate).format('YYYY-MM-DD'));

  useEffect(() => {
    setSelectedKey(moment(anchorDate).format('YYYY-MM-DD'));
  }, [anchorDate]);

  const { weeks, statsByKey, monthLabel } = useMemo(
    () => buildMonthCalendarModel(data, anchorDate, inventoryByDate, totalRooms),
    [anchorDate, data, inventoryByDate, totalRooms],
  );

  const selectedDay = moment(selectedKey, 'YYYY-MM-DD');
  const selectedSnap =
    statsByKey[selectedKey] || buildDayStat(inventoryByDate, data, selectedKey, totalRooms);
  const selectedLoad = getLoadPctRaw(selectedSnap);
  const selectedTrend = getDayTrend(statsByKey, selectedDay, anchorDate, selectedSnap);
  const TrendIcon =
    selectedTrend === 'up' ? TrendingUp : selectedTrend === 'down' ? TrendingDown : TrendingFlat;

  const handleDayTap = (day, key, inMonth) => {
    if (!inMonth) return;
    setSelectedKey(key);
    onDateSelect?.(day.toDate());
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.85 }}>
      <Box
        sx={{
          ...mobileAppGlassCard,
          p: 1.1,
          borderRadius: '16px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.85 }}>
          <Typography sx={{ color: '#f8fafc', fontWeight: 800, fontSize: '1rem', letterSpacing: '-0.02em' }}>
            {monthLabel}
          </Typography>
          <Chip
            size="small"
            label="Full month"
            sx={{
              height: 22,
              fontSize: '0.62rem',
              fontWeight: 700,
              bgcolor: alpha(LOGIN_ACCENT, 0.14),
              color: LOGIN_ACCENT,
              border: `1px solid ${alpha(LOGIN_ACCENT, 0.35)}`,
            }}
          />
        </Box>

        <Stack direction="row" spacing={0.5} sx={{ mb: 0.85, flexWrap: 'wrap', gap: 0.5 }}>
          {legendItems.map((item) => (
            <Box
              key={item.label}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.45,
                px: 0.75,
                py: 0.25,
                borderRadius: 999,
                bgcolor: alpha('#fff', 0.04),
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 8,
                  borderRadius: '4px',
                  bgcolor: item.background,
                  border: `1px solid ${item.border}`,
                }}
              />
              <Typography sx={{ fontSize: '0.62rem', color: '#e2e8f0', fontWeight: 600 }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gap: 0.35,
            mb: 0.35,
          }}
        >
          {WEEKDAY_LABELS.map((label, index) => (
            <Typography
              key={`${label}-${index}`}
              sx={{
                textAlign: 'center',
                fontSize: '0.62rem',
                fontWeight: 700,
                color: '#cbd5e1',
              }}
            >
              {label}
            </Typography>
          ))}
        </Box>

        {weeks.map((week, weekIndex) => (
          <Box
            key={weekIndex}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 0.35,
              mb: 0.35,
            }}
          >
            {week.map((day) => {
              const key = day.format('YYYY-MM-DD');
              const inMonth = day.month() === moment(anchorDate).month();
              const snap = statsByKey[key] || buildDayStat(inventoryByDate, data, key, totalRooms);
              const loadPctRaw = getLoadPctRaw(snap);
              const cellColors = mobileDarkOccupancyCellColors(loadPctRaw, inMonth);
              const occupancyLabel = formatCellOccupancyLabel(snap);
              const isToday = key === todayStr;
              const isSelected = key === selectedKey;

              return (
                <Box
                  key={key}
                  role={inMonth ? 'button' : undefined}
                  tabIndex={inMonth ? 0 : -1}
                  aria-label={inMonth ? `${day.format('D MMMM')}, ${loadPctRaw > 0 ? `${formatLoadPctLabel(loadPctRaw)}% load` : 'no bookings'}` : undefined}
                  onClick={() => handleDayTap(day, key, inMonth)}
                  onKeyDown={(event) => {
                    if (!inMonth) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleDayTap(day, key, inMonth);
                    }
                  }}
                  sx={{
                    border: isSelected
                      ? `2px solid ${LOGIN_ACCENT}`
                      : isToday
                        ? `1.5px solid ${alpha(LOGIN_ACCENT, 0.65)}`
                        : `1px solid ${cellColors.border}`,
                    borderRadius: '10px',
                    background: cellColors.background,
                    opacity: inMonth ? 1 : 0.28,
                    p: 0.35,
                    minHeight: 0,
                    aspectRatio: '1 / 1',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    cursor: inMonth ? 'pointer' : 'default',
                    boxShadow: isSelected ? `0 0 16px ${alpha(LOGIN_ACCENT, 0.28)}` : 'none',
                    WebkitTapHighlightColor: 'transparent',
                    userSelect: 'none',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 0.2,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.78rem',
                        fontWeight: 800,
                        color: `${cellColors.dayColor} !important`,
                        WebkitTextFillColor: cellColors.dayColor,
                        lineHeight: 1.1,
                        textShadow: '0 1px 3px rgba(0,0,0,0.85)',
                        flexShrink: 0,
                      }}
                    >
                      {day.format('D')}
                    </Box>
                    {inMonth && loadPctRaw > 0 ? (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 0.35,
                          py: 0.15,
                          borderRadius: '5px',
                          bgcolor: 'rgba(0,0,0,0.55)',
                          border: '1px solid rgba(255,255,255,0.14)',
                          maxWidth: '100%',
                          flexShrink: 1,
                          fontSize: '0.58rem',
                          fontWeight: 800,
                          color: `${cellColors.pctColor} !important`,
                          WebkitTextFillColor: cellColors.pctColor,
                          lineHeight: 1.05,
                          letterSpacing: '-0.02em',
                          textShadow: '0 1px 2px rgba(0,0,0,0.9)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatLoadPctLabel(loadPctRaw)}%
                      </Box>
                    ) : null}
                  </Box>
                  {inMonth ? (
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.1,
                        minHeight: 0,
                        px: 0.1,
                        py: 0.15,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.2,
                          fontSize: '0.5rem',
                          fontWeight: 700,
                          color: 'rgba(148,163,184,0.95) !important',
                          WebkitTextFillColor: 'rgba(148,163,184,0.95)',
                          lineHeight: 1,
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                        }}
                      >
                        <FlightLand sx={{ fontSize: 10, color: LOGIN_ACCENT }} />
                        {snap.arrivals}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: '0.68rem',
                          fontWeight: 800,
                          color: `${cellColors.dayColor} !important`,
                          WebkitTextFillColor: cellColors.dayColor,
                          lineHeight: 1.05,
                          letterSpacing: '-0.03em',
                          textShadow: '0 1px 3px rgba(0,0,0,0.85)',
                        }}
                      >
                        {occupancyLabel.primary}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: '0.48rem',
                          fontWeight: 700,
                          color: 'rgba(148,163,184,0.88) !important',
                          WebkitTextFillColor: 'rgba(148,163,184,0.88)',
                          lineHeight: 1,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        {occupancyLabel.secondary}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1 }} />
                  )}
                  {inMonth ? (
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, loadPctRaw)}
                      sx={{
                        height: 4,
                        borderRadius: 999,
                        bgcolor: cellColors.barTrack,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 999,
                          bgcolor: cellColors.barColor,
                          boxShadow: loadPctRaw > 0 ? `0 0 6px ${alpha(cellColors.barColor, 0.55)}` : 'none',
                        },
                      }}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          ...mobileAppGlassCard,
          p: 1.1,
          borderRadius: '16px',
          borderColor: alpha(LOGIN_ACCENT, 0.22),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.65 }}>
          <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.88rem' }}>
            {selectedDay.format('dddd, D MMM')}
          </Typography>
          <TrendIcon
            sx={{
              fontSize: 18,
              color:
                selectedTrend === 'up'
                  ? '#81c784'
                  : selectedTrend === 'down'
                    ? '#ef9a9a'
                    : 'rgba(148,163,184,0.8)',
            }}
          />
        </Box>

        <Typography sx={{ color: 'rgba(148,163,184,0.92)', fontSize: '0.72rem', mb: 0.85 }}>
          {formatDaySummary(selectedSnap, selectedDay)}
        </Typography>

        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
          <Chip
            size="small"
            icon={<FlightLand sx={{ fontSize: '14px !important' }} />}
            label={`${selectedSnap.arrivals} arrivals`}
            sx={{
              height: 26,
              fontSize: '0.68rem',
              bgcolor: alpha('#fff', 0.05),
              color: '#e2e8f0',
              border: '1px solid rgba(255,255,255,0.08)',
              '& .MuiChip-icon': { color: LOGIN_ACCENT },
            }}
          />
          <Chip
            size="small"
            icon={<MeetingRoom sx={{ fontSize: '14px !important' }} />}
            label={
              selectedSnap.mode === 'inventory'
                ? `${selectedSnap.bookedNights}/${selectedSnap.capacity} nights`
                : `${selectedSnap.occupied}/${selectedSnap.totalRooms} rooms`
            }
            sx={{
              height: 26,
              fontSize: '0.68rem',
              bgcolor: alpha('#fff', 0.05),
              color: '#e2e8f0',
              border: '1px solid rgba(255,255,255,0.08)',
              '& .MuiChip-icon': { color: '#ffb74d' },
            }}
          />
          {selectedLoad > 0 ? (
            <Chip
              size="small"
              label={`${formatLoadPctLabel(selectedLoad)}% load`}
              sx={{
                height: 26,
                fontSize: '0.68rem',
                bgcolor: alpha('#fff', 0.08),
                color: selectedLoad > 100 ? '#fca5a5' : selectedLoad >= 70 ? '#fde68a' : '#cbd5e1',
                border: `1px solid ${selectedLoad > 100 ? alpha('#ef4444', 0.35) : alpha('#fff', 0.12)}`,
              }}
            />
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}
